import { useEffect } from 'react';
import type { CalendarBooking } from '@/types/calendar';
import { BOOKING_STATUS_COLORS, PAYMENT_STATUS_COLORS } from '@/types/calendar';

interface BookingModalProps {
  booking: CalendarBooking | null;
  isOpen: boolean;
  onClose: () => void;
  onViewCustomer: (customerId: string) => void;
}

export default function BookingModal({
  booking,
  isOpen,
  onClose,
  onViewCustomer,
}: BookingModalProps) {
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen || !booking) {
    return null;
  }

  const statusColors = BOOKING_STATUS_COLORS[booking.status];
  const paymentColors = PAYMENT_STATUS_COLORS[booking.paymentStatus];

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }).format(date);
  };

  const formatTime = (date: Date) => {
    return new Intl.DateTimeFormat('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    }).format(date);
  };

  const formatPrice = (price: number, currency: string = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
    }).format(price);
  };

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={handleBackdropClick}
    >
      <div className="relative max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-lg bg-white shadow-xl">
        {/* Header */}
        <div className="sticky top-0 border-b border-gray-200 bg-white px-6 py-4">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h2 className="text-xl font-semibold text-gray-900">
                {booking.serviceName}
              </h2>
              <p className="mt-1 text-sm text-gray-500">{booking.businessName}</p>
            </div>
            <button
              onClick={onClose}
              className="ml-4 rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
              aria-label="Close modal"
            >
              <svg
                className="h-5 w-5"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            <span
              className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-medium ${statusColors.bg} ${statusColors.text}`}
            >
              {booking.status.replace('_', ' ')}
            </span>
            <span
              className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-medium ${paymentColors.bg} ${paymentColors.text}`}
            >
              Payment: {booking.paymentStatus}
            </span>
          </div>
        </div>

        {/* Content */}
        <div className="space-y-6 p-6">
          {/* Customer Section */}
          <div>
            <h3 className="text-sm font-medium text-gray-900">Customer</h3>
            <div className="mt-3 space-y-2">
              <div className="flex items-center text-sm">
                <svg
                  className="mr-3 h-5 w-5 text-gray-400"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                <span className="text-gray-900">{booking.customerName}</span>
              </div>
              {booking.customerPhone && (
                <div className="flex items-center text-sm">
                  <svg
                    className="mr-3 h-5 w-5 text-gray-400"
                    fill="none"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  <span className="text-gray-600">{booking.customerPhone}</span>
                </div>
              )}
              {booking.customerEmail && (
                <div className="flex items-center text-sm">
                  <svg
                    className="mr-3 h-5 w-5 text-gray-400"
                    fill="none"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <span className="text-gray-600">{booking.customerEmail}</span>
                </div>
              )}
            </div>
          </div>

          {/* Time Section */}
          <div>
            <h3 className="text-sm font-medium text-gray-900">Date & Time</h3>
            <div className="mt-3 space-y-2">
              <div className="flex items-center text-sm">
                <svg
                  className="mr-3 h-5 w-5 text-gray-400"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <span className="text-gray-900">{formatDate(booking.scheduledAt)}</span>
              </div>
              <div className="flex items-center text-sm">
                <svg
                  className="mr-3 h-5 w-5 text-gray-400"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-gray-600">
                  {formatTime(booking.scheduledAt)} - {formatTime(booking.endsAt)}
                  <span className="ml-2 text-gray-500">
                    ({booking.durationMinutes} min)
                  </span>
                </span>
              </div>
            </div>
          </div>

          {/* Staff Section */}
          {booking.staffName && (
            <div>
              <h3 className="text-sm font-medium text-gray-900">Staff Member</h3>
              <div className="mt-3">
                <div className="flex items-center text-sm">
                  <svg
                    className="mr-3 h-5 w-5 text-gray-400"
                    fill="none"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path d="M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0zm6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="text-gray-900">{booking.staffName}</span>
                </div>
              </div>
            </div>
          )}

          {/* Payment Section */}
          {booking.price !== undefined && (
            <div>
              <h3 className="text-sm font-medium text-gray-900">Payment</h3>
              <div className="mt-3 space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Amount</span>
                  <span className="text-lg font-semibold text-gray-900">
                    {formatPrice(booking.price, booking.currency)}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Notes Section */}
          {booking.notes && (
            <div>
              <h3 className="text-sm font-medium text-gray-900">Notes</h3>
              <div className="mt-3 rounded-lg bg-gray-50 p-4">
                <p className="text-sm text-gray-700 whitespace-pre-wrap">
                  {booking.notes}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 border-t border-gray-200 bg-gray-50 px-6 py-4">
          <div className="flex justify-end gap-3">
            <button
              onClick={onClose}
              className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Close
            </button>
            {booking.customerId && (
              <button
                onClick={() => {
                  onViewCustomer(booking.customerId!);
                  onClose();
                }}
                className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
              >
                View Customer
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
